import React, { useState, ChangeEvent, FocusEvent } from 'react';
import { InputInfo } from '../Profile';
import { ValidationResult } from '../../utils/validation';

interface SocialMediaInputProps {
    icon: React.ReactNode;
    value: string;
    onChange: (value: string) => void;
    onBlur: (value: string) => void;
    placeholder: string;
    validate?: (value: string) => ValidationResult;
    maxLength?: number;
    /** Parent validation after Save (e.g. invalid value never committed because blur only runs when valid) */
    externalError?: string;
}

const SocialMediaInput: React.FC<SocialMediaInputProps> = ({
    icon,
    value,
    onChange,
    onBlur,
    placeholder,
    validate,
    maxLength = 100,
    externalError
}) => {
    const [error, setError] = useState<string | undefined>();
    const [isTouched, setIsTouched] = useState(false);

    const displayError = externalError || (isTouched ? error : undefined);

    const handleInputChange = (event: ChangeEvent<HTMLInputElement> | string | number | undefined) => {
        let stringValue: string;
        if (typeof event === 'object' && 'target' in event) {
            stringValue = event.target.value;
        } else {
            stringValue = String(event || '');
        }

        onChange(stringValue);
        if (isTouched && validate) {
            const result = validate(stringValue);
            setError(result.error);
        }
    };

    const handleInputBlur = (event: FocusEvent<HTMLInputElement> | string | number | undefined) => {
        let stringValue: string;
        if (typeof event === 'object' && 'target' in event) {
            stringValue = event.target.value;
        } else {
            stringValue = String(event || '');
        }

        setIsTouched(true);
        if (validate) {
            const result = validate(stringValue);
            setError(result.error);
            if (result.isValid) {
                onBlur(stringValue);
            }
        } else {
            onBlur(stringValue);
        }
    };

    return (
        <div className="flex flex-col w-full min-w-0">
            <div className="flex w-full min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white dark:bg-white [&_svg]:!h-6 [&_svg]:!w-6">
                    {icon}
                </div>
                <div className="min-w-0 flex-1">
                    <InputInfo
                        label=""
                        value={value}
                        placeholder={placeholder}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        maxLength={maxLength}
                        aria-invalid={!!displayError}
                        aria-describedby={displayError ? `${placeholder}-error` : undefined}
                        className={displayError ? 'border-red-500' : ''}
                    />
                </div>
            </div>
            {displayError && (
                <div
                    id={`${placeholder}-error`}
                    className="mt-1 ml-[3.25rem] text-xs text-red-500"
                    role="alert"
                >
                    {displayError}
                </div>
            )}
        </div>
    );
};

export default SocialMediaInput;
